import { Injectable } from '@angular/core';
import { StompService } from '@stomp/ng2-stompjs';
import { AuthenticationService } from '../../shared/service/authentication.service';
import { Observable } from 'rxjs/Observable';
import { Coordinate } from '../../shared/model/coordinate';
import { Subject } from 'rxjs/Subject';
import { Vehicle } from '../../shared/model/vehicle';
import { CoordinateDiagnosis } from '../../shared/model/coordinate-diagnosis';
import { EquipmentDiagnosis } from '../../shared/model/equipment-diagnosis';
import { GpsDiagnosis } from '../../shared/model/gps-diagnosis';
import { GprsDiagnosis } from '../../shared/model/gprs-diagnosis';
import { DiagnosisGeneralResult } from '../../shared/model/diagnosis-general-result';
import { Customer } from '../../shared/model/customer';

@Injectable()
export class DiagnosisWebSocketService {
  private RUN_END_POINT = '/api/diagnosis/run';
  private SUCCESS_END_POINT = '/diagnosis/success/';
  private FAIL_END_POINT = '/diagnosis/failed/';
  private RESPONSE_END_POINT = '/diagnosis/response/';
  private RUNNING_END_POINT = '/diagnosis/running/';
  private TIMEOUT_END_POINT = '/diagnosis/timeout/';
  private ERROR_END_POINT = '/diagnosis/error/';

  private userHashCode: number;
  private customer: Customer;
  private vehicle: Vehicle;

  private successSubscription: Observable<any> = null;
  private failedSubscription: Observable<any> = null;
  private responseSubscription: Observable<any> = null;
  private runningSubscription: Observable<any> = null;
  private timeoutSubscription: Observable<any> = null;
  private errorSubscription: Observable<any> = null;

  success$: Observable<any>;
  failed$: Observable<any>;
  response$: Observable<any>;
  restart$: Observable<any>;
  vehicle$: Observable<any>;
  running$: Observable<any>;
  timeout$: Observable<any>;
  back$: Observable<any>;
  error$: Observable<any>;

  private successSubject: Subject<any>;
  private failedSubject: Subject<any>;
  private responseSubject: Subject<any>;
  private restartSubject: Subject<any>;
  private vehicleSubject: Subject<any>;
  private runningSubject: Subject<any>;
  private timeoutSubject: Subject<any>;
  private backSubject: Subject<any>;
  private errorSubject: Subject<any>;

  constructor(private _webSocket: StompService,
              private _authService: AuthenticationService) {
    this.userHashCode = this.getUserHashCode();
    this.successSubject = new Subject();
    this.success$ = this.successSubject.asObservable();
    this.failedSubject = new Subject();
    this.failed$ = this.failedSubject.asObservable();
    this.responseSubject = new Subject();
    this.response$ = this.responseSubject.asObservable();
    this.restartSubject = new Subject();
    this.restart$ = this.restartSubject.asObservable();
    this.vehicleSubject = new Subject();
    this.vehicle$ = this.vehicleSubject.asObservable();
    this.runningSubject = new Subject();
    this.running$ = this.runningSubject.asObservable();
    this.timeoutSubject = new Subject();
    this.timeout$ = this.timeoutSubject.asObservable();
    this.backSubject = new Subject();
    this.back$ = this.backSubject.asObservable();
    this.errorSubject = new Subject();
    this.error$ = this.errorSubject.asObservable();
    this.getWebSocketSubscriptions();
  }

  run(vehicle: Vehicle) {
    if (this.customer) {
      this.setVehicle(vehicle);
      this.notifyVehicleIsAvailable();
      this.subscribeSuccess();
      this.subscribeFailed();
      this.subscribeResponse();
      this.subscribeRunning();
      this.subscribeTimeout();
      this.subscribeError();
      this._webSocket.publish(this.RUN_END_POINT, JSON.stringify(this.vehicle));
    } else {
      throw new Error();
    }
  }

  restart() {
    this.restartSubject.next();
    this.notifyVehicleIsAvailable();
    this._webSocket.publish(this.RUN_END_POINT, JSON.stringify(this.vehicle));
  }

  back() {
    this.backSubject.next();
  }

  setCustomer(customer: Customer) {
    this.customer = customer;
  }

  getCustomer() {
    return this.customer;
  }

  setVehicle(vehicle: Vehicle) {
    vehicle.userHashCode = this.userHashCode;
    vehicle.customerName = this.customer.name;
    vehicle.trixUserId = this.customer.trixUser.id;
    this.vehicle = vehicle;
  }

  notifyVehicleIsAvailable() {
    if (this.vehicle) {
      this.vehicleSubject.next(this.vehicle);
    }
  }

  getVehicle() {
    return this.vehicle;
  }

  getUserHashCode() {
    return this._authService.getUserFromLocalStorage().userHashCode;
  }

  private parseResult(result) {
    result = JSON.parse(result);
    return Object.assign(this.getDiagnosisResultInstance(result), result);
  }

  private getDiagnosisResultInstance(result) {
    switch (result.type) {
      case 'EQUIPMENT': return new EquipmentDiagnosis();
      case 'COORDINATE': return new CoordinateDiagnosis();
      case 'GPS': return new GpsDiagnosis();
      case 'GPRS': return new GprsDiagnosis();
      default: return {};
    }
  }

  private getWebSocketSubscriptions() {
    this.successSubscription =
      this._webSocket.subscribe(`${this.SUCCESS_END_POINT}${this.userHashCode}`);

    this.failedSubscription =
      this._webSocket.subscribe(`${this.FAIL_END_POINT}${this.userHashCode}`);

    this.responseSubscription =
      this._webSocket.subscribe(`${this.RESPONSE_END_POINT}${this.userHashCode}`);

    this.runningSubscription =
      this._webSocket.subscribe(`${this.RUNNING_END_POINT}${this.userHashCode}`);

    this.timeoutSubscription =
      this._webSocket.subscribe(`${this.TIMEOUT_END_POINT}${this.userHashCode}`);

    this.errorSubscription =
      this._webSocket.subscribe(`${this.ERROR_END_POINT}${this.userHashCode}`);
  }

  private subscribeSuccess() {
    this.successSubscription
      .map((message: any) => {
        return this.parseResult(message.body);
      })
      .subscribe((message: any) => this.successSubject.next(message));
  }

  private subscribeFailed() {
    this.failedSubscription
      .map((message: any) => {
        return this.parseResult(message.body);
      })
      .subscribe((message: any) => this.failedSubject.next(message));
  }

  private subscribeResponse() {
    this.responseSubscription
      .map((message: any) => {
        const result = JSON.parse(message.body);
        if (Array.isArray(result)) {
          return Object.assign(new Array<Coordinate>(), result);
        } else {
          return Object.assign(new DiagnosisGeneralResult(), result);
        }
      })
      .subscribe((message: any) => this.responseSubject.next(message));
  }

  private subscribeRunning() {
    this.runningSubscription
      .map((message: any) => {
        return this.parseResult(message.body);
      })
      .subscribe((message: any) => this.runningSubject.next(message));
  }

  private subscribeTimeout() {
    this.timeoutSubscription
      .map((message: any) => {
        return this.parseResult(message.body);
      })
      .subscribe((message: any) => this.timeoutSubject.next(message));
  }

  private subscribeError() {
    this.errorSubscription
      .map((message: any) => {
        return this.parseResult(message.body);
      })
      .subscribe((message: any) => this.errorSubject.next(message));
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/production-argos-front/argos/src/app/diagnosis/service/diagnosis-web-socket.service.ts